import React from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import OrderDetailSectionCard from '../components/OrderDetailSectionCard';
import { StepSection } from '../process/OrderProcessParts';
import InvoiceCoverageSummary, { InvoiceCoverageTag } from '../../invoices/components/InvoiceCoverageSummary';
import OrderSalesInvoicesTable from '../../invoices/components/OrderSalesInvoicesTable';
import type { InvoiceCoverage } from '../../../types/invoiceCoverage';
import type { OrderSalesInvoice } from '../../../types/orderSalesInvoice';
import type { AdvanceInvoiceBalance } from '../../invoices/api/invoiceWorkflowApi';
import InvoiceAdvanceBalance from '../../invoices/components/InvoiceAdvanceBalance';

interface OrderInvoicesProps {
  coverage: InvoiceCoverage | null | undefined;
  /** null = not loaded or not visible to this reader. */
  salesInvoices?: OrderSalesInvoice[] | null;
  /** The list could not be loaded (not „no invoices”): shown with a retry. */
  salesInvoicesFailed?: boolean;
  onRetrySalesInvoices?: () => void;
  onInvoicesChanged?: () => void;
  /** Advances and settlement invoices against the ZK value; null without advances. */
  advanceBalance?: AdvanceInvoiceBalance | null;
}

/** Whether the ZK has anything to show: an active invoice or an invoice document. */
export function hasOrderInvoices({ coverage, salesInvoices, salesInvoicesFailed }: OrderInvoicesProps): boolean {
  return Boolean((coverage && coverage.state !== 'NONE') || salesInvoices?.length || salesInvoicesFailed);
}

/**
 * Invoices of a ZK, shown on the „Faktura” stage and in „Szczegóły zamówienia”:
 *   - „Faktury sprzedaży” (sales document readers, `salesInvoices` loaded):
 *     every invoice with its state and amounts, „Usuń fakturę” (decision
 *     2026-10-19: a draft is deleted, an approved invoice withdrawn);
 *   - WP11 coverage: invoiced / remaining quantity per line and, for readers
 *     of sales invoices, which invoice covers how much of the line.
 */
export function OrderInvoicesBody({ coverage, salesInvoices = null, salesInvoicesFailed = false, onRetrySalesInvoices, onInvoicesChanged, advanceBalance = null }: OrderInvoicesProps) {
  const { t } = useTranslation('invoices');
  const hasCoverage = Boolean(coverage && coverage.state !== 'NONE');
  const hasDocuments = Boolean(salesInvoices?.length);
  return <Space direction="vertical" size={16} style={{ width: '100%' }}>
    {salesInvoicesFailed && <Alert type="warning" showIcon data-testid="order-sales-invoices-failed"
      message={t('invoices:orderInvoices.loadFailed')}
      action={onRetrySalesInvoices && <Button size="small" onClick={onRetrySalesInvoices}>{t('invoices:orderInvoices.retry')}</Button>} />}
    {advanceBalance && <InvoiceAdvanceBalance balance={advanceBalance} />}
    {hasDocuments && <OrderSalesInvoicesTable invoices={salesInvoices!} onChanged={() => onInvoicesChanged?.()} />}
    {hasCoverage && <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {hasDocuments && <Typography.Text strong>{t('invoices:orderInvoices.coverageTitle')}</Typography.Text>}
      <InvoiceCoverageSummary coverage={coverage} showInvoiceList={!hasDocuments} />
    </Space>}
  </Space>;
}

/**
 * „Faktury sprzedaży” section of the ZK card (decision 2026-10-19): inside
 * the „Faktura” stage, and below the current stage of any earlier one while
 * the ZK has invoices (a partial invoice, or one left after a withdrawal reopened
 * the ZK) — the later „Faktura” stage cannot be opened from there.
 */
export function OrderInvoicesStepSection(props: OrderInvoicesProps & { title: React.ReactNode }) {
  if (!hasOrderInvoices(props)) return null;
  return <StepSection title={props.title} testId="zk-sales-invoices" aside={<InvoiceCoverageTag coverage={props.coverage} />}>
    <OrderInvoicesBody {...props} />
  </StepSection>;
}

/**
 * „Fakturowanie zamówienia” in the ZK „Szczegóły zamówienia” drawer; hidden
 * while there is neither an active invoice nor an invoice document.
 */
export default function OrderInvoiceCoverageSection({ defaultOpen = false, ...props }: OrderInvoicesProps & { defaultOpen?: boolean }) {
  const { t } = useTranslation('invoices');
  if (!hasOrderInvoices(props)) return null;
  return <OrderDetailSectionCard title={t('invoices:workflow.coverage.title')} extra={<InvoiceCoverageTag coverage={props.coverage} />}
    defaultOpen={defaultOpen} data-testid="order-invoice-coverage">
    <OrderInvoicesBody {...props} />
  </OrderDetailSectionCard>;
}
